import React, { useEffect } from 'react'
import { DataFaker, LOGGER } from '@cicero/data/utils'
import { DateTime } from 'luxon'
import { makeStyles, MenuItem, TextField, Chip } from '@material-ui/core'
import { useForm } from '../forms'
import { DraggableLayout, IDraggableClasses, IDraggableZone, getDragResultDelta, updateAfterDragResult } from '../layout/DraggableLayout'

export const DraggableLayoutDemo = () => {
  const {
    calCell,
    metaCell,
    glistGroup,
    glistPlacementCell,
    glistMetaCell,
    kanbanHeader,
    kanbanCell,
    kanbanPlacementCell,
    calMonthHeader,
    calWeekPlacementCell,
    calWeekCell,
    calWeekMeta,
  } = useStyles()
  const $form = useForm({
    onSubmit: (result) => { LOGGER.debug('submitted', result) },
    initialValues: () => ({
      layout: 'kanban',
      classes: {} as IDraggableClasses,
      zones: [] as IDraggableZone[][],
    }),
  })

  useEffect(() => {
    let zones: IDraggableZone[][] = []
    let classes: IDraggableClasses = {}
    switch ($form.values.layout) {
      case 'kanban':
        zones = kanbanZones
        classes = { metaCell, cell: kanbanCell, placementList: kanbanPlacementCell }
        break
      case 'groupedList':
        zones = groupedListZones
        classes = { placementCell: glistPlacementCell, placementList: glistGroup }
        break
      case 'cal-month':
        zones = calendarMonthZones
        classes = { placementCell: calCell, metaCell }
        break
      case 'cal-week':
        zones = calendarWeekZones
        classes = { cell: calWeekCell, placementCell: calWeekPlacementCell, metaCell: calWeekMeta }
        break
    }
    $form.setValues({ ...$form.values, zones, classes })
  }, [$form.values.layout])

  // - `Calendar`
  //   - `CalendarDay`
  //   - `CalendarWeek`
  //   - `CalendarMonth`
  //   - `CalendarAgenda`
  // - `Grouped Draggables` - Split results into seperate subgroups and can be dragged between them. Requires an attribute with options.
  //   - `GroupedList`
  //   - `Kanban`
  //   - `CardView`
  //   - `TableList`

  return <>
    <TextField label="Job" select required variant="outlined" {...$form.props('layout')}>
      { ['kanban', 'groupedList', 'cal-month', 'cal-week'].map(val => <MenuItem key={val} value={val}>{val}</MenuItem>) }
    </TextField>
    <br /><br />
    <DraggableLayout
      classes={$form.values.classes}
      zones={$form.values.zones}
      renderPlacementHeader={(zone) => {
        switch ($form.values.layout) {
          case 'kanban':
            return zone.title ? <span className={kanbanHeader}>
              <Chip label={zone.title} />
            </span> : null
          case 'groupedList':
            return zone.title ? <span className={glistMetaCell}>
              <Chip label={zone.title} />
            </span> : null
          case 'cal-month':
            return zone.title ? <span className={calMonthHeader}>{zone.title}</span> : null
          default: return null
        }
      }}
      renderMetaSection={(zone) => {
        switch ($form.values.layout) {
          case 'cal-week':
            if (zone.title && zone.time) {
              return <span className={calMonthHeader}>{zone.title}</span>
            }
        }
        return zone.title ? <Chip label={zone.title} /> : null
      }}
      onDragEnd={(result) => {
        const { sourceAddress, sourceZone, sourceCard, targetAddress, targetZone } = getDragResultDelta(result, $form.values.zones)
        if (sourceZone?.cards && targetZone?.cards && sourceAddress && targetAddress) {
          $form.setValues({
            ...$form.values,
            zones: updateAfterDragResult($form.values.zones, { sourceAddress, targetAddress, card: sourceCard }),
          })
        }
      }}
    />
  </>
}

const kanbanZones: IDraggableZone[][] = [
  ['No Status', 'Blocked', 'In Progress', 'Testing', 'Completed'].map((title) => {
    const seedBase = DataFaker.genSeed(title)
    const cardCount = DataFaker.gen(DataFaker.DATE_MONTHS, seedBase)
    const cards = DataFaker.genFactory((seed) => ({
      title: `${DataFaker.gen(DataFaker.DRINKS, seedBase + seed)}`,
    }), cardCount)
    return { id: title.toLowerCase().replaceAll(' ', '_'), title, cards }
  }),
]

const groupedListZones: IDraggableZone[][] = [
  ...['Sprint 1', 'Sprint 2', 'Sprint 3', 'Sprint 4', 'Backlog'].reduce((rows: IDraggableZone[][], title, i) => {
    // rows.push([{ id: title.toLowerCase(), title }])
    const seedBase = DataFaker.genSeed(title)
    const cardCount = DataFaker.gen(DataFaker.DATE_MONTHS, i * seedBase)
    const cards = DataFaker.genFactory((seed) => ({
      title: `${DataFaker.gen(DataFaker.VERBS, seedBase * seed)} ${DataFaker.gen(DataFaker.NOUNS, seedBase + seed)}`,
    }), cardCount)
    rows.push([{ id: `${title.toLowerCase()}-values`, title, cards }])
    return rows
  }, []),
]

const calendarMonthZones: IDraggableZone[][] = [
  DataFaker.DATE_DAY_NAMES.map(title => ({ id: title.toLowerCase(), title })),
  ...Array(5).fill(undefined).map((z, i) => {
    const row: IDraggableZone[] = []
    DataFaker.DATE_DAY_NAMES.forEach((day, j) => {
      const id = `${day}-${j}-${i}`
      const seedBase = DataFaker.genSeed(id)
      const cardCount = Math.max(0, DataFaker.gen(DataFaker.DATE_MONTHS, seedBase) - 7)
      const cards = DataFaker.genFactory((seed) => ({
        title: `${DataFaker.gen(DataFaker.VERBS, seedBase + seed * i)} ${DataFaker.gen(DataFaker.NOUNS, seedBase + seed - j)}`,
      }), cardCount)
      row.push({ id, title: `${(i + 1) * (j + 1)}`, cards })
    })
    return row
  }),
]

const calendarWeekZones: IDraggableZone[][] = [
  [
    { id: 'empty' },
    ...DataFaker.DATE_DAY_NAMES.map(title => ({ id: title.toLowerCase(), title })),
  ],
  ...Array(24).fill(undefined).map((s, i) => {
    const minutes = i * 60
    const display = DateTime.fromObject({ hour: Math.floor(minutes / 60), minute: minutes % 60 })
    return [
      { id: display.toFormat('hh:mm'), time: true, title: minutes % 60 === 0 ? display.toFormat('hh:mm') : '' },
      ...Array(7).fill(undefined).map((t, j) => {
        const cardCount = Math.max(0, DataFaker.gen(DataFaker.DATE_MONTHS, i * DataFaker.genSeed(`${minutes * j}`)) - 9)
        const cards = DataFaker.genFactory((seed) => ({
          title: `${DataFaker.gen(DataFaker.VERBS, seed * j * minutes)} ${DataFaker.gen(DataFaker.NOUNS, seed * i)}`,
        }), cardCount)
        return { id: `${i}-${j}`, cards }
      }),
    ]
  }),
]

const useStyles = makeStyles({
  kanbanHeader: {
    display: 'block',
    // textAlign: 'center',
    padding: 16,
  },
  kanbanCell: {
    border: 0,
  },
  kanbanPlacementCell: {
    minHeight: 250,
  },
  glistMetaCell: {
    padding: '32px 16px 0',
    border: 0,
    // borderTop: '1px solid #CCC',
  },
  glistPlacementCell: {
    padding: '6px 0',
    border: 0,
  },
  glistGroup: {
    // background: '#EEE',
    width: '100%',
  },
  calCell: {
    height: 200,
    border: '1px solid #EEE',
  },
  calMonthHeader: {
    color: '#CCC',
    padding: 4,
  },
  calWeekPlacementCell: {
    height: 70,
    overflow: 'visible',
    position: 'relative',
  },
  calWeekCell: {
    borderRight: '1px solid #EEE',
    borderBottom: '1px solid #EEE',
  },
  calWeekMeta: {
    padding: 6,
    textAlign: 'center',
  },
  metaCell: {
    padding: 6,
    textAlign: 'center',
  },
})
